/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch } from "react";
import dayjs from "dayjs";
import { Text } from "@nordcloud/gnui";

import {
  GetCloudResourceFilterValuesQuery,
  CloudResourceProvider,
} from "~/generated/graphql";
import { Mode, MultiTagsInput } from "~/components";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableDateRange } from "~/components/SidebarWithMultiSelect/SelectableDateRange";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { Item } from "~/components/SidebarWithMultiSelect/types";
import { dateFormat, VM_TYPES } from "~/constants";
import { isNotEmpty, isNotNil } from "~/tools";
import { useGetCloudAccountsFilterValues } from "../../hooks";
import { LifeSpanInput } from "../Filters";
import { State } from "./types";

type Props = {
  state: State;
  updateState: Dispatch<Partial<State>>;
  filtersData?: GetCloudResourceFilterValuesQuery;
  resourceType: string;
};

// eslint-disable-next-line complexity
export function AttributesFilters(props: Props) {
  const { state, updateState, filtersData, resourceType } = props;

  const { data: cloudAccountsData } = useGetCloudAccountsFilterValues();

  const clearOperatingSystemFilter = () => {
    updateState({ operatingSystems: [], excludeOperatingSystems: false });
  };

  const clearCloudProvidersFilter = () => {
    updateState({ cloudProviders: [], excludeCloudProviders: false });
  };

  const clearRegionsFilter = () => {
    updateState({ regions: [], excludeRegions: false });
  };

  const clearCloudAccountsFilter = () => {
    updateState({ cloudAccounts: [], excludeCloudAccounts: false });
  };

  const clearCloudServicesFilter = () => {
    updateState({ cloudServices: [], excludeCloudServices: false });
  };

  const clearResourceCategoriesFilter = () => {
    updateState({ resourceCategories: [], excludeResourceCategories: false });
  };

  const clearResourceTypesFilter = () => {
    updateState({ resourceTypes: [], excludeResourceTypes: false });
  };

  const clearResourceTagNamesAndTagValuesFilter = () => {
    updateState({
      resourceTagNamesAndTagValues: [],
      excludeResourceTagNamesAndTagValues: false,
    });
  };

  const clearResourceMetadataKeysAndValuesFilter = () => {
    updateState({
      resourceMetadataKeysAndValues: [],
      excludeResourceMetadataKeysAndValues: false,
    });
  };

  const clearDiscoveryTimesFilter = () => {
    updateState({
      discoveryTimeRange: {
        startDateUTC: undefined,
        endDateUTC: undefined,
      },
      excludeDiscoveryTimeRange: false,
    });
  };

  const clearResourceLifeSpansFilter = () => {
    updateState({
      resourceLifeSpanRange: {
        from: undefined,
        to: undefined,
      },
      excludeResourceLifeSpanRange: false,
    });
  };

  const clearResourceGroupsFilter = () => {
    updateState({
      resourceGroups: [],
      excludeResourceGroups: false,
    });
  };

  const clearPlansFilter = () => {
    updateState({ plans: [], excludePlans: false });
  };

  const resourceTypesCheckboxes =
    filtersData?.cloudResourceFilterValues.resourceTypes.map(
      (resourceTypes) => {
        return {
          value: resourceTypes,
          label: resourceTypes,
        } as Item;
      }
    );

  const regionsCheckboxes = filtersData?.cloudResourceFilterValues.regions.map(
    (region) => {
      return {
        value: region,
        label: region,
      } as Item;
    }
  );

  const cloudServicesCheckboxes =
    filtersData?.cloudResourceFilterValues.cloudServices.map((cloudService) => {
      return {
        value: cloudService,
        label: cloudService,
      } as Item;
    });

  const cloudResourceProviderCheckboxes: Item[] = [
    {
      value: CloudResourceProvider.Aws,
      label: CloudResourceProvider.Aws,
    },
    {
      value: CloudResourceProvider.Azure,
      label: CloudResourceProvider.Azure,
    },
    {
      value: CloudResourceProvider.Gc,
      label: CloudResourceProvider.Gc,
    },
    {
      value: CloudResourceProvider.Ibm,
      label: CloudResourceProvider.Ibm,
    },
    {
      value: CloudResourceProvider.Other,
      label: CloudResourceProvider.Other,
    },
  ];

  const cloudAccountsCheckboxes = cloudAccountsData?.cloudAccounts?.result?.map(
    (cloudAccount) => {
      return {
        value: cloudAccount?.id,
        label: cloudAccount?.id,
        subLabel: `${cloudAccount?.name} | ${cloudAccount?.provider}`,
      } as Item;
    }
  );

  return (
    <>
      <Text size="sm" weight="bold">
        Attributes
      </Text>
      <FilterWrapper
        name="Operating System"
        isSelected={
          isNotNil(state.operatingSystems) && isNotEmpty(state.operatingSystems)
        }
        selectedOptions={state.operatingSystems ?? []}
        onClear={clearOperatingSystemFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.operatingSystems ?? []}
          selectableItems={[]}
          isExcludeSelected={state.excludeOperatingSystems}
          onMultiSelect={(selections) => {
            updateState({ operatingSystems: selections });
          }}
          onModeChange={(mode) => {
            updateState({ excludeOperatingSystems: mode === Mode.EXCLUDES });
          }}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Cloud Providers"
        isSelected={
          isNotNil(state.cloudProviders) && isNotEmpty(state.cloudProviders)
        }
        selectedOptions={state.cloudProviders ?? []}
        onClear={clearCloudProvidersFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.cloudProviders ?? []}
          selectableItems={cloudResourceProviderCheckboxes}
          isExcludeSelected={state.excludeCloudProviders}
          onMultiSelect={(selections) => {
            updateState({ cloudProviders: selections });
          }}
          onModeChange={(mode) => {
            updateState({ excludeCloudProviders: mode === Mode.EXCLUDES });
          }}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Regions"
        isSelected={isNotNil(state.regions) && isNotEmpty(state.regions)}
        selectedOptions={state.regions ?? []}
        onClear={clearRegionsFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.regions ?? []}
          selectableItems={regionsCheckboxes ?? []}
          isExcludeSelected={state.excludeRegions}
          onMultiSelect={(selections) => {
            updateState({ regions: selections });
          }}
          onModeChange={(mode) => {
            updateState({ excludeRegions: mode === Mode.EXCLUDES });
          }}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Cloud Accounts"
        isSelected={
          isNotNil(state.cloudAccounts) && isNotEmpty(state.cloudAccounts)
        }
        selectedOptions={state.cloudAccounts ?? []}
        onClear={clearCloudAccountsFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.cloudAccounts ?? []}
          selectableItems={cloudAccountsCheckboxes ?? []}
          isExcludeSelected={state.excludeCloudAccounts}
          onMultiSelect={(selections) => {
            updateState({ cloudAccounts: selections });
          }}
          onModeChange={(mode) => {
            updateState({ excludeCloudAccounts: mode === Mode.EXCLUDES });
          }}
        />
      </FilterWrapper>
      {resourceType !== VM_TYPES && (
        <FilterWrapper
          name="Cloud Services"
          isSelected={
            isNotNil(state.cloudServices) && isNotEmpty(state.cloudServices)
          }
          selectedOptions={state.cloudServices ?? []}
          onClear={clearCloudServicesFilter}
        >
          <SelectableListWithSearch
            allowModeChange
            selectedOptions={state.cloudServices ?? []}
            selectableItems={cloudServicesCheckboxes ?? []}
            isExcludeSelected={state.excludeCloudServices}
            onMultiSelect={(selections) => {
              updateState({ cloudServices: selections });
            }}
            onModeChange={(mode) => {
              updateState({ excludeCloudServices: mode === Mode.EXCLUDES });
            }}
          />
        </FilterWrapper>
      )}
      {resourceType !== VM_TYPES && (
        <FilterWrapper
          name="Resource Categories"
          isSelected={
            isNotNil(state.resourceCategories) &&
            isNotEmpty(state.resourceCategories)
          }
          selectedOptions={state.resourceCategories ?? []}
          onClear={clearResourceCategoriesFilter}
        >
          <SelectableListWithSearch
            allowModeChange
            selectedOptions={state.resourceCategories ?? []}
            selectableItems={[]}
            isExcludeSelected={state.excludeResourceCategories}
            onMultiSelect={(selections) => {
              updateState({ resourceCategories: selections });
            }}
            onModeChange={(mode) => {
              updateState({
                excludeResourceCategories: mode === Mode.EXCLUDES,
              });
            }}
          />
        </FilterWrapper>
      )}
      {resourceType !== VM_TYPES && (
        <FilterWrapper
          name="Resource Types"
          isSelected={
            isNotNil(state.resourceTypes) && isNotEmpty(state.resourceTypes)
          }
          selectedOptions={state.resourceTypes ?? []}
          onClear={clearResourceTypesFilter}
        >
          <SelectableListWithSearch
            allowModeChange
            selectedOptions={state.resourceTypes ?? []}
            selectableItems={resourceTypesCheckboxes ?? []}
            isExcludeSelected={state.excludeResourceTypes}
            onMultiSelect={(selections) => {
              updateState({ resourceTypes: selections });
            }}
            onModeChange={(mode) => {
              updateState({ excludeResourceTypes: mode === Mode.EXCLUDES });
            }}
          />
        </FilterWrapper>
      )}
      <FilterWrapper
        name="Resource Tag Names and Tag Values"
        isSelected={
          isNotNil(state.resourceTagNamesAndTagValues) &&
          isNotEmpty(state.resourceTagNamesAndTagValues)
        }
        selectedOptions={state.resourceTagNamesAndTagValues ?? []}
        onClear={clearResourceTagNamesAndTagValuesFilter}
      >
        <div style={{ padding: "8px" }}>
          <MultiTagsInput
            inputTags={state.resourceTagNamesAndTagValues ?? []}
            isExcludeSelected={state.excludeResourceTagNamesAndTagValues}
            onModeChange={(mode) => {
              updateState({
                excludeResourceTagNamesAndTagValues: mode === Mode.EXCLUDES,
              });
            }}
            onChange={(selections) => {
              updateState({ resourceTagNamesAndTagValues: selections });
            }}
            placeholderName={"Tag"}
          />
        </div>
      </FilterWrapper>
      <FilterWrapper
        name="Resource Metadata Keys and Values"
        isSelected={
          isNotNil(state.resourceMetadataKeysAndValues) &&
          isNotEmpty(state.resourceMetadataKeysAndValues)
        }
        selectedOptions={state.resourceMetadataKeysAndValues ?? []}
        onClear={clearResourceMetadataKeysAndValuesFilter}
      >
        <div style={{ padding: "8px" }}>
          <MultiTagsInput
            inputTags={state.resourceMetadataKeysAndValues ?? []}
            placeholderName="Metadata"
            isExcludeSelected={state.excludeResourceMetadataKeysAndValues}
            onModeChange={(mode) => {
              updateState({
                excludeResourceMetadataKeysAndValues: mode === Mode.EXCLUDES,
              });
            }}
            onChange={(selections) => {
              updateState({ resourceMetadataKeysAndValues: selections });
            }}
          />
        </div>
      </FilterWrapper>
      <FilterWrapper
        name="Discovery Time"
        isSelected={
          !!state?.discoveryTimeRange.startDateUTC &&
          !!state?.discoveryTimeRange.endDateUTC
        }
        selectedOptions={["startDateUTC", "endDateUTC"]}
        onClear={clearDiscoveryTimesFilter}
      >
        <SelectableDateRange
          allowModeChange
          selectedRange={{
            startDate: dayjs(state.discoveryTimeRange.startDateUTC)
              .format(dateFormat.shortDate)
              .toString(),
            endDate: dayjs(state.discoveryTimeRange.endDateUTC)
              .format(dateFormat.shortDate)
              .toString(),
          }}
          onRangeSelect={(range) => {
            updateState({
              discoveryTimeRange: {
                startDateUTC: range.startDate
                  ? dayjs(range.startDate)
                      .format(dateFormat.fullDate)
                      .toString()
                  : undefined,
                endDateUTC: range.endDate
                  ? dayjs(range.endDate).format(dateFormat.fullDate).toString()
                  : undefined,
              },
            });
          }}
          label="The time range when the resource was discovered"
          setDefaultEndDateToToday
          onModeChange={(mode) => {
            updateState({
              excludeDiscoveryTimeRange: mode === Mode.EXCLUDES,
            });
          }}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Resource Life Span"
        isSelected={
          (!!state?.resourceLifeSpanRange.from ||
            state?.resourceLifeSpanRange.from === 0) &&
          (!!state?.resourceLifeSpanRange.to ||
            state?.resourceLifeSpanRange.to === 0)
        }
        selectedOptions={["0", "0"]}
        onClear={clearResourceLifeSpansFilter}
      >
        <div style={{ padding: "8px" }}>
          <LifeSpanInput
            filters={{
              lifeSpanFrom: state.resourceLifeSpanRange.from,
              lifeSpanTo: state.resourceLifeSpanRange.to,
            }}
            updateFilters={(input) => {
              updateState({
                resourceLifeSpanRange: {
                  from: input.lifeSpanFrom,
                  to: input.lifeSpanTo,
                },
              });
            }}
            onModeChange={(mode) => {
              updateState({
                excludeResourceLifeSpanRange: mode === Mode.EXCLUDES,
              });
            }}
          />
        </div>
      </FilterWrapper>
      <FilterWrapper
        name="Resource Group"
        isSelected={
          isNotNil(state.resourceGroups) && isNotEmpty(state.resourceGroups)
        }
        selectedOptions={state.resourceGroups ?? []}
        onClear={clearResourceGroupsFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.resourceGroups ?? []}
          selectableItems={[]}
          isExcludeSelected={state.excludeResourceGroups}
          onMultiSelect={(selections) => {
            updateState({
              resourceGroups: selections,
            });
          }}
          onModeChange={(mode) => {
            updateState({
              excludeResourceGroups: mode === Mode.EXCLUDES,
            });
          }}
        />
      </FilterWrapper>
      <FilterWrapper
        name="Plan"
        isSelected={isNotNil(state.plans) && isNotEmpty(state.plans)}
        selectedOptions={state.plans ?? []}
        onClear={clearPlansFilter}
      >
        <SelectableListWithSearch
          allowModeChange
          selectedOptions={state.plans ?? []}
          selectableItems={[]}
          isExcludeSelected={state.excludePlans}
          onMultiSelect={(selections) => {
            updateState({
              plans: selections,
            });
          }}
          onModeChange={(mode) => {
            updateState({
              excludePlans: mode === Mode.EXCLUDES,
            });
          }}
        />
      </FilterWrapper>
    </>
  );
}
